import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { BasePageView } from "../../../../../common/components/BasePageView";
import { SoundsResourcePackageWrapper } from "../../../../../common/resources/SoundsResourcePackageWrapper";
import { PromiseCompletionSource } from "../../../../../../../shared/async/PromiseCompletionSource";
import { ICanvasScaler } from "../../../../../../../canvas/ICanvasScaler";
import { GiftResourcePackage } from "../../../resources/GiftResourcePackage";
import { TopPanel } from "by/gamefactory/cityversetycoon/scenes/game/components/toppanel/TopPanel";
import { ShieldContainer } from "../../../components/giftsummary/shield/ShieldContainer";
import { IGiftBoxPopupController } from "../../../controller/IGiftBoxPopupController";
import { gsap } from "gsap";
import { SlowMo } from "gsap/EasePack";
import { GiftShieldsAction } from "../../../../../game/flow/impl/diceActions/GiftShieldsAction";
import { GameFont } from "../../../../../common/GameFont";
import { FullscreenInputBlocker } from "../../../../../game/components/FullscreenInputBlocker";

export class GiftShieldsPage extends BasePageView {
    private static readonly SHIELD_TOOL_TIP_TEXT = "YOU JUST GOT A SHIELD,\nSHIELD PROTECTS YOUR BUILDING";

    private readonly commonResources: CommonResourcesPackage;
    private readonly giftResources: GiftResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly viewCompletionSource: PromiseCompletionSource<void>;
    private readonly giftBoxPopupController: IGiftBoxPopupController;
    private readonly shieldsAction: GiftShieldsAction;
    private readonly isFirstShow: boolean;
    private topPanel: TopPanel;
    private shieldContainer: ShieldContainer;
    private shieldToolTip: Sprite;
    private currentAnimation: gsap.core.Timeline;

    public constructor(
        commonResources: CommonResourcesPackage,
        giftResources: GiftResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        canvasScaler: ICanvasScaler,
        giftBoxPopupController: IGiftBoxPopupController,
        shieldsAction: GiftShieldsAction,
        viewCompletionSource: PromiseCompletionSource<void>,
        isFirstShow: boolean,
        topPanel: TopPanel
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.giftResources = giftResources;
        this.soundResources = soundResources;
        this.giftBoxPopupController = giftBoxPopupController;
        this.shieldsAction = shieldsAction;
        this.viewCompletionSource = viewCompletionSource;
        this.isFirstShow = isFirstShow;
        this.topPanel = topPanel;
        this.build();
        this.once("added", this.show.bind(this));
        this.eventMode = "static";
        this.on("pointerup", this.skipAnimations, this);
    }

    protected override build(): void {
        const fadeScreen = new FullscreenInputBlocker(this.canvasScaler)
        this.addChild(fadeScreen);
        if (this.isFirstShow) {
            this.shieldToolTip = new Sprite(this.commonResources.assets_common_ui_tooltip_bg_png);
            this.shieldToolTip.scale.set(1.1);
            this.shieldToolTip.anchor.set(0.5, 0.5);
            this.addChild(this.shieldToolTip);
            const shieldToolTipText = new Text(GiftShieldsPage.SHIELD_TOOL_TIP_TEXT, new ToolTipTextStyle());
            shieldToolTipText.anchor.set(0.5, 0.6);
            this.shieldToolTip.addChild(shieldToolTipText);
        }
        this.shieldContainer = new ShieldContainer(this.giftResources.assets_views_gift_ui_shields_shield_png, this.shieldsAction.diceRollGiftRewardInShields);
        this.addChild(this.shieldContainer);
        this.topPanel.bringToFront();
        super.build();
    }

    private async show(): Promise<void> {
        this.soundResources.assets_sounds_Collect_Screen_mp3.play();
        this.topPanel.show();
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.fromTo(this.shieldContainer.scale, { x: 0.2, y: 0.2 }, { x: 1, y: 1, duration: 0.3, ease: "power0.out" });
        animationTimeLine.fromTo(this.shieldContainer, { y: this.shieldContainer.y + 400, angle: 70 }, { y: this.shieldContainer.y, angle: 0, duration: 0.3, ease: "power0.out" }, "<");
        if (this.isFirstShow) {
            animationTimeLine.fromTo(this.shieldToolTip.scale, { x: 0.1, y: 0.1 }, { x: 1, y: 1, duration: 0.4 }, "<");
            animationTimeLine.fromTo(this.shieldToolTip, { y: this.shieldToolTip.y + 500, angle: -70, alpha: 0 }, { y: this.shieldToolTip.y, angle: this.shieldToolTip.angle, alpha: 1, duration: 0.4 }, "<");
            animationTimeLine.to(this.shieldToolTip.scale, { x: 0, y: 0, delay: 1.5, duration: 0.3 });
        }
        animationTimeLine.addPause("+=0.3");
        await animationTimeLine.play();
        animationTimeLine.kill();
        await this.startCollecting();
    }

    private async hidePage(): Promise<void> {
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.1, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
        this.viewCompletionSource.complete();
    }

    private async startCollecting(): Promise<void> {
        await this.createAndFlyShieldReward();
        this.giftBoxPopupController.collectShieldHandle(this.shieldsAction.diceRollGiftRewardInShields);
        this.hidePage();
    }

    private async createAndFlyShieldReward(): Promise<void> {
        const shieldIconReward = new Sprite(this.commonResources.assets_common_ui_top_panel_shield_png);
        shieldIconReward.scale.set(0.2, 0.2);
        shieldIconReward.anchor.set(0.5, 0.5);
        shieldIconReward.position = this.shieldContainer.position;
        this.addChild(shieldIconReward);
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.to(shieldIconReward.scale, { x: 1, y: 0.6, duration: 0.8, ease: SlowMo.ease.config(0.05, 0.1, true) });
        animationTimeLine.to(shieldIconReward, { angle: shieldIconReward.angle - 360, x: this.topPanel.x + this.topPanel.width * 0.97, y: this.topPanel.y + this.topPanel.height * 0.5, duration: 0.8 }, "<");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private skipAnimations(): void {
        if (this.currentAnimation != null && this.currentAnimation.isActive()) {
            this.currentAnimation.progress(1);
        }
    }

    protected override layout(width: number, height: number): void {
        this.shieldContainer.position.set(width * 0.5, height * 0.45);
        if (this.isFirstShow) {
            this.shieldToolTip.position.set(width * 0.5, this.shieldContainer.y - this.shieldContainer.height * 0.5 - this.shieldToolTip.height * 0.4);
        }
        super.layout(width, height);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerup", this.skipAnimations, this);
        this.topPanel.bringToOriginParent();
        super.destroy(options);
    }
}

class ToolTipTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 44,
            strokeThickness: 1,
            leading: 15
        }));
    }
}
